import React, { useState } from "react";
import { Modal, Button, Table, Form, Alert, InputGroup } from "react-bootstrap";
import moment from "moment";
import { useBillingActions } from "../../hooks/useBillingActions";

const REFUND_REASONS = [
  { value: "requested_by_customer", label: "Requested by customer" },
  { value: "duplicate", label: "Duplicate charge" },
  { value: "fraudulent", label: "Fraudulent" },
];

const TransactionDetailsModal = ({
  show,
  payment,
  onClose,
  onDownloadReceipt,
  getStatusClass,
  companyId,
}) => {
  const { refund } = useBillingActions(companyId);
  const [showRefund, setShowRefund] = useState(false);
  const [refundType, setRefundType] = useState("full"); // "full" | "partial"
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [formError, setFormError] = useState(null);

  if (!payment) return null;

  const originalAmount = Number(payment.paymentAmount || 0);

  // Refund is only offered for a settled card payment we can trace back to a
  // Stripe PaymentIntent (referenceId starts with "pi_").
  const paymentIntentId =
    typeof payment.referenceId === "string" &&
    payment.referenceId.startsWith("pi_")
      ? payment.referenceId
      : null;
  const canRefund =
    Boolean(companyId) &&
    Boolean(paymentIntentId) &&
    payment.paymentStatus === "Paid" &&
    originalAmount > 0;

  const resetRefundForm = () => {
    setShowRefund(false);
    setRefundType("full");
    setPartialAmount("");
    setReason("requested_by_customer");
    setFormError(null);
  };

  const handleConfirmRefund = () => {
    setFormError(null);
    let amount = null; // null = full refund

    if (refundType === "partial") {
      const parsed = Number(partialAmount);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setFormError("Enter a valid amount greater than 0.");
        return;
      }
      if (parsed > originalAmount) {
        setFormError(
          `Refund cannot exceed the original payment of $${originalAmount.toFixed(2)}.`
        );
        return;
      }
      amount = parsed;
    }

    refund.mutate(
      { paymentIntentId, amount, reason },
      {
        onSuccess: () => {
          resetRefundForm();
          onClose();
        },
      }
    );
  };

  return (
    <>
      <Modal show={show && !showRefund} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table responsive bordered>
            <tbody>
              <tr>
                <th style={{ width: "30%" }}>Status</th>
                <td>
                  <span className={getStatusClass(payment.paymentStatus)}>
                    {payment.paymentStatus}
                  </span>
                </td>
              </tr>
              <tr>
                <th>Type</th>
                <td>{payment.paymentType}</td>
              </tr>
              <tr>
                <th>Users</th>
                <td>{payment.paymentPlanUser}</td>
              </tr>
              <tr>
                <th>Amount</th>
                <td>${originalAmount.toFixed(2)}</td>
              </tr>
              <tr>
                <th>Date</th>
                <td>
                  {payment.dateOfPayment
                    ? moment(payment.dateOfPayment).format("MMM DD, YYYY HH:mm")
                    : "N/A"}
                </td>
              </tr>
              {payment.invoiceId && (
                <tr>
                  <th>Invoice ID</th>
                  <td>{payment.invoiceId}</td>
                </tr>
              )}
              {payment.subscriptionId && (
                <tr>
                  <th>Subscription ID</th>
                  <td>{payment.subscriptionId}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canRefund && (
            <Button
              variant="outline-danger"
              onClick={() => setShowRefund(true)}
            >
              Refund
            </Button>
          )}
          {payment.invoice_url && (
            <Button
              variant="primary"
              onClick={() => onDownloadReceipt(payment.invoice_url)}
            >
              Download Receipt
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Refund confirmation modal */}
      <Modal show={show && showRefund} onHide={resetRefundForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Refund Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small mb-3">
            <strong>How refunds work:</strong> The money is returned to the
            customer's original card through Stripe. Card refunds usually take{" "}
            <strong>5–10 business days</strong> to appear on their statement. A
            full refund returns the entire ${originalAmount.toFixed(2)}; a
            partial refund returns only the amount you enter (it cannot exceed
            the original payment). Refunds <strong>cannot be undone</strong>,
            and this does not change the subscription status.
          </Alert>

          <p className="mb-2">
            <strong>Original payment:</strong> ${originalAmount.toFixed(2)} (
            {payment.paymentType})
          </p>

          <Form.Group className="mb-3">
            <Form.Check
              type="radio"
              id="refund-full"
              name="refundType"
              label={`Full refund ($${originalAmount.toFixed(2)})`}
              checked={refundType === "full"}
              onChange={() => {
                setRefundType("full");
                setFormError(null);
              }}
            />
            <Form.Check
              type="radio"
              id="refund-partial"
              name="refundType"
              label="Partial refund"
              checked={refundType === "partial"}
              onChange={() => setRefundType("partial")}
            />
          </Form.Group>

          {refundType === "partial" && (
            <Form.Group className="mb-3">
              <Form.Label>Refund amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={originalAmount}
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  placeholder={`Up to ${originalAmount.toFixed(2)}`}
                />
              </InputGroup>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Reason</Form.Label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REFUND_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {formError && (
            <Alert variant="danger" className="small mb-0">
              {formError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={resetRefundForm}
            disabled={refund.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRefund}
            disabled={refund.isPending}
          >
            {refund.isPending
              ? "Processing…"
              : refundType === "full"
                ? `Refund $${originalAmount.toFixed(2)}`
                : `Refund $${Number(partialAmount || 0).toFixed(2)}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TransactionDetailsModal;
